/**
 * @coeldery/screen-top-bar — 每頁頂欄（**左／中／右 slot**）
 *
 * 為何係模組：任何「每個畫面頂部有工具列」嘅 app 都用得（第二個 app 一定用得到）→ R3 做成模組。
 * ⚠️ 查庫證據（R1，2026-09-22）：庫內 `top-bar` 1.0.0 **唔合用** —— `scope=product:coeldery-family-tree`
 *   （產品專屬）、綁死 LaoKa `appLinks`、而且明文「禁止橙色／粉紅」（本產品 primary 正是珊瑚橙）。
 *
 * 血緣：cosingery（2026-09-22 產品負責人 UI／UX 五改：`decisions/023`「add a top bar in each tap」）
 *
 * 設計：
 *   · 高度 ＝ `var(--topbar-h, 56px)`；`sticky`（預設）＋ safe-area-inset-top
 *   · 三個 slot 由父層組裝（**產品文案唔准寫死喺模組**；文字一律由 props 入）
 *   · 只有 `title`（文字）而冇 `center` 時 → 自動置中顯示
 *   · 顏色／圓角一律 CSS var；熱區 ≥44×44px（由父層放入嘅掣負責）
 */
import type { ReactNode } from 'react';

export interface ScreenTopBarProps {
  /** 左邊（例：〔＋〕新對話） */
  left?: ReactNode;
  /** 中間（例：〔管理歌詞〕） */
  center?: ReactNode;
  /** 右邊（例：〔開始作歌〕） */
  right?: ReactNode;
  /** 標題文字（冇 `center` 時置中顯示；例：頁名） */
  title?: string;
  /** 預設 sticky（跟頁面捲動） */
  sticky?: boolean;
  /**
   * ⭐ 1.2.0：呢條欄**係唔係頁面最頂**？
   *   · `false`（預設）＝ 上面仲有 app 自己嘅品牌列（佢已經處理咗 safe-area）→ **唔准再加**
   *     （血證 2026-09-22 產品負責人：頂欄空白兩行 ＝ 品牌列同頂欄**各自**加咗一次
   *      `env(safe-area-inset-top)`）
   *   · `true` ＝ 呢條欄自己就係最頂 → 由佢負責 safe-area
   */
  safeAreaTop?: boolean;
}

export default function ScreenTopBar({ left, center, right, title, sticky = true, safeAreaTop = false }: ScreenTopBarProps) {
  return (
    <div
      data-testid="screen-top-bar"
      style={{
        position: sticky ? 'sticky' : 'static',
        top: 0,
        zIndex: 20,
        /** ⭐ 1.1.0：**唔准被壓縮**（父親係 flex column 時，否則內容多會壓扁頂欄） */
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        /* ⭐ 1.2.0：精簡高度（產品負責人：「at least 2 rows empty space wasted」） */
        minHeight: 'var(--topbar-h, 44px)',
        padding: '3px 12px',
        paddingTop: safeAreaTop ? 'calc(3px + env(safe-area-inset-top, 0px))' : '3px',
        background: 'color-mix(in srgb, var(--color-bg) 92%, transparent)',
        backdropFilter: 'blur(8px)',
        borderBottom: '1px solid var(--color-divider)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '0 0 auto' }}>{left}</div>
      <div
        style={{
          flex: '1 1 auto',
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}
      >
        {center ?? (
          <span
            style={{
              fontSize: 'var(--font-size-label, 1.125rem)',
              fontWeight: 800,
              color: 'var(--color-text)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '0 0 auto' }}>{right}</div>
    </div>
  );
}
